import ReduxStore from 'src/types/store/reduxStore';
import { Entity, Conditions, RangeFilter } from 'sl-api-connector/types';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getDayIdFromDate, buildFullMonthRangeFilters } from 'src/utils/dateformatting';
import { buildAggregations } from 'src/components/AdManager/Search';
import { Option } from 'funfix-core';
import { store } from 'src/main';
import { fetchEntityMetrics } from 'src/store/modules/entitySearchService/operations';
import { getSearchAppName } from 'src/utils/app';
import _isNil from 'lodash/isNil';
import _last from 'lodash/last';
import moment from 'moment';
import { warn } from 'logrocket';

export const computeDataSet = (
  metricsByDayId: Map<number, { actual?: number; projected?: number; budgetAmount: number }>
) => {
  const startDate = moment().startOf('month');
  const endDate = moment().endOf('month');

  const metrics = {
    dailyBudget: [] as [number, number][],
    surplusBudget: [] as [number, number][],
    overspend: [] as [number, number][],
    padding: [] as [number, number][],
    actualSpend: [] as [number, number][],
    projectedSpend: [] as [number, number][],
    totalCampaignBudgetSum: 0 as number
  };
  let curDate = startDate.clone();
  let totalCampaignBudgetSum = 0;
  while (curDate.isBefore(endDate)) {
    const dayId = getDayIdFromDate(curDate.toDate());
    const timestamp = +curDate.format('x');

    // Get the spend for the week ID in question and then multiply by the daily multiplier
    let metricsForDay = metricsByDayId.get(dayId);
    if (!metricsForDay || (_isNil(metricsForDay.actual) && _isNil(metricsForDay.projected))) {
      warn(`Missing both daily spend metrics + projections for day id ${dayId}`);
      metricsForDay = { projected: 0, budgetAmount: 0 };
    }
    const { actual: actualSpend, projected: projectedSpend, budgetAmount: dailyBudget } = metricsForDay;
    metrics.dailyBudget.push([timestamp, dailyBudget]);
    totalCampaignBudgetSum += dailyBudget;
    const adSpendForDay = (metricsForDay.projected ? projectedSpend : actualSpend) || 0;
    if (metricsForDay.projected) {
      // Use projected spend metrics if we have no actual data and the dayId isn't in the past.
      metrics.projectedSpend.push([timestamp, adSpendForDay]);
      metrics.actualSpend.push([timestamp, 0]);
    } else {
      // Use actual spend metrics if we have them
      metrics.actualSpend.push([timestamp, adSpendForDay]);
      metrics.projectedSpend.push([timestamp, 0]);
    }

    const surplusBudget = dailyBudget - adSpendForDay;
    if (surplusBudget >= 0) {
      metrics.surplusBudget.push([timestamp, surplusBudget]);
      metrics.overspend.push([timestamp, 0]);
    } else {
      metrics.surplusBudget.push([timestamp, 0]);
      metrics.overspend.push([timestamp, -surplusBudget]);

      if (metricsForDay.projected) {
        _last(metrics.projectedSpend)![1] += surplusBudget;
      } else {
        _last(metrics.actualSpend)![1] += surplusBudget;
      }
    }

    curDate = curDate.add(1, 'day');
  }

  const maxValue = metrics.actualSpend.reduce(
    (acc, [_ts, actualSpend], i) =>
      Math.max(acc, actualSpend + metrics.projectedSpend[i][1] + metrics.overspend[i][1] + metrics.surplusBudget[i][1]),
    0
  );
  metrics.actualSpend.forEach((_, i) => {
    const timestamp = metrics.actualSpend[i][0];
    const padding =
      maxValue -
      (metrics.actualSpend[i][1] +
        metrics.projectedSpend[i][1] +
        metrics.overspend[i][1] +
        metrics.surplusBudget[i][1]);

    metrics.padding.push([timestamp, padding]);
  });

  metrics.totalCampaignBudgetSum = totalCampaignBudgetSum;

  return metrics;
};

export const fetchDailySpendAndProjections = async (
  baseDataKey: string,
  { app, retailer, mainEntity }: Pick<ReduxStore, 'app' | 'retailer'> & { mainEntity: Entity },
  conditions: Conditions
) => {
  const fetchData = async (dataKey: string, indexName: string, fields: string[], isProjected: number) => {
    const timePeriodRangeFilters: RangeFilter[] = buildFullMonthRangeFilters();
    const metricFields = fields.map((fieldName) =>
      INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, 'campaign', 'dayId')
    );
    const groupByField = INDEX_FIELDS.getField(getSearchAppName(), indexName, 'dayId');
    let useAggregationSort = true;
    const baseKey = baseDataKey.split('-')[0];

    // We only want to turn off aggregation based sort for the donutChart & dailyAdSpendBarChart
    if (['scoreDonutChart', 'dailyAdSpendBarChart'].includes(baseKey)) {
      useAggregationSort = false;
    }

    const [{ aggregations: aggregationFields }] = buildAggregations(metricFields);
    const queryConditions = {
      // Strip out `categoryId` conditions since they don't work with `dayId` aggregations on these special indices
      termFilters: Option.of(conditions.termFilters)
        .map((termFilters) => termFilters.filter(({ fieldName }) => fieldName !== 'categoryId'))
        .getOrElse([
          {
            fieldName: 'isProjected',
            condition: 'must',
            values: [isProjected]
          }
        ]),
      rangeFilters: timePeriodRangeFilters
    };
    if (queryConditions.termFilters.findIndex((x) => x.fieldName === 'isProjected') === -1) {
      queryConditions.termFilters.push({
        fieldName: 'isProjected',
        condition: 'must',
        values: [isProjected]
      });
    }

    await store.dispatch(
      fetchEntityMetrics(
        dataKey,
        {
          entity: mainEntity,
          retailer,
          app: { ...app, name: getSearchAppName(), apiAppName: getSearchAppName() },
          indexName
        },
        [
          {
            doAggregation: true,
            aggregations: [
              {
                aggregationFields,
                conditions: {
                  termFilters: [{ fieldName: 'retailerId', values: [+retailer.id] }],
                  rangeFilters: timePeriodRangeFilters
                },
                groupByFieldName: groupByField.name
              }
            ],
            conditions: queryConditions,
            shouldCache: false,
            useAggregationSort,
            pageSize: 100
          }
        ]
      )
    );
  };

  // Fetch projections to use when no real data is available
  await fetchData(`${baseDataKey}-projections`, 'adCampaignDailyMetrics', ['spend', 'budgetAmount', 'isProjected'], 1);
  await fetchData(`${baseDataKey}-actual`, 'adCampaignDailyMetrics', ['spend', 'budgetAmount', 'isProjected'], 0);
};

export const generateBudgetPacingCSV = (data) => {
  const headers = ['Date', 'Daily Budget', 'Surplus Budget', 'Overspend', 'Padding', 'Actual Spend'];

  const rows = data.dailyBudget.map((item, index) => {
    const date = new Date(item[0]).toISOString().split('T')[0]; // Format timestamp to YYYY-MM-DD
    return [
      date,
      item[1],
      data.surplusBudget[index][1],
      data.overspend[index][1],
      data.padding[index][1],
      data.actualSpend[index][1]
    ];
  });

  const csvContent = [
    headers.join(','), // Add headers
    ...rows.map((row) => row.join(',')) // Add rows
  ].join('\n');

  return csvContent;
};
